import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { loadFieldPolicy, readRecordRaw, runFieldEdit } from '../command-center/pursuit/fieldEditRun.js'
import { factPolicy, factRevision, validFactRecord, recordWithFacts, performFactEdit } from './durableFacts.js'

export function useDurableFacts(champ, demo) {
  const [state, setState] = useState({ status: 'loading', raw: null, policy: null })
  const [busy, setBusy] = useState(false)
  const [reload, setReload] = useState(0)
  const current = useRef(null)
  const inFlight = useRef(false)
  const generation = useRef(0)
  const champId = String(champ?.id || '')
  // Updated during render, not in a later effect: a response cannot repaint a
  // newly navigated record in the gap before effects run.
  const ready = state.status === 'ready' && String(state.raw?.id) === champId
  const revision = ready ? factRevision(state.raw) : ''
  current.current = { champId, revision }
  useEffect(() => {
    const turn = ++generation.current
    const controller = new AbortController()
    inFlight.current = false; setBusy(false)
    setState({ status: demo ? 'demo' : 'loading', raw: null, policy: null })
    if (!champId || demo) return () => { generation.current = turn + 1; controller.abort() }
    Promise.all([readRecordRaw(champId, { signal: controller.signal }), loadFieldPolicy({ signal: controller.signal })])
      .then(([raw, policy]) => {
        if (turn !== generation.current) return
        if (!validFactRecord(raw, champId)) throw new Error('The direct read did not return this record and all three dossier fields.')
        setState({ status: 'ready', raw, policy: factPolicy(policy) })
      }).catch(error => {
        if (turn === generation.current && error.name !== 'AbortError') setState({ status: 'error', raw: null, policy: null, error: error.message })
      })
    return () => { generation.current = turn + 1; controller.abort() }
  }, [champId, demo, reload])
  const record = useMemo(() => ready ? recordWithFacts(champ, state.raw) : champ, [champ, ready, state.raw])
  const save = useCallback(async input => {
    if (inFlight.current || !ready || demo) return { state: 'blocked', message: 'Wait for the current record and any pending correction.' }
    inFlight.current = true; setBusy(true)
    const turn = generation.current
    const isCurrent = expected => turn === generation.current && current.current.champId === String(input.raw.id) && current.current.revision === expected
    const result = await performFactEdit(input, { loadPolicy: loadFieldPolicy, readRecord: readRecordRaw, writeField: runFieldEdit, isCurrent })
    if (turn === generation.current) {
      if (result.state === 'saved') setState(previous => ({ ...previous, raw: result.raw }))
      else {
        // Even a refused response may follow a partial/uncertain server path.
        // Never re-enable outbound from the pre-write snapshot. Only a fresh
        // direct reload can establish the facts used by copy and later edits.
        setState(previous => ({ ...previous, status: 'error', raw: null, error: result.message }))
      }
      inFlight.current = false; setBusy(false)
    }
    return result
  }, [ready, demo])
  return { ...state, ready, busy, revision, record, save, reload: () => setReload(value => value + 1) }
}
